import { useQuery } from "@tanstack/react-query";
import { AppWindow, ArrowUpCircle, Boxes, ExternalLink, LayoutGrid, Loader2, ShieldCheck, Sparkles, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import type { AppCatalogItem, AppInstallationRecord, AppInstallationStatus } from "@hostdock/types";
import { CatalogAppLogo, cn, ErrorState, formatRelative, Skeleton } from "@hostdock/ui";
import { api } from "../api";
import { EmptyCard, FilterTabs, PageHero, Pill, type PILL_TONES, SearchBox, StatCard, Toolbar } from "../components/page-kit";

export const CATEGORY_LABEL: Record<string, string> = {
  cms: "Websites & blogs", commerce: "Online stores", community: "Forums & communities", wiki: "Wikis & docs", business: "Business", support: "Help desk",
  files: "Files & sharing", education: "Education", analytics: "Analytics", developer: "Developer tools", utilities: "Utilities", media: "Photos & media",
  email: "Email", survey: "Surveys & forms",
};

export const STATUS: Record<AppInstallationStatus, { label: string; tone: keyof typeof PILL_TONES }> = {
  PENDING: { label: "Queued", tone: "info" }, INSTALLING: { label: "Installing", tone: "info" }, ACTIVE: { label: "Active", tone: "ok" }, FAILED: { label: "Failed", tone: "bad" },
  UPDATING: { label: "Updating", tone: "info" }, BACKING_UP: { label: "Backing up", tone: "info" }, RESTORING: { label: "Restoring", tone: "info" },
  UNINSTALLING: { label: "Uninstalling", tone: "wait" }, UNINSTALLED: { label: "Uninstalled", tone: "off" },
};
export const busy = (s: AppInstallationStatus) => ["PENDING", "INSTALLING", "UPDATING", "BACKING_UP", "RESTORING", "UNINSTALLING"].includes(s);

type Tab = "browse" | "installed";

/** App Installer: the catalog of reviewed apps and the customer's installations. */
export function AppsPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "installed" ? "installed" : "browse";
  const catalog = useQuery({ queryKey: ["apps"], queryFn: api.apps.catalog });
  const installs = useQuery({
    queryKey: ["app-installations"], queryFn: api.apps.installations,
    refetchInterval: (q) => (q.state.data?.items.some((i) => busy(i.status)) ? 3000 : 30_000),
  });
  const items = installs.data?.items ?? [];
  const updates = items.filter((i) => i.actions.includes("update")).length;
  const unhealthy = items.filter((i) => i.status === "ACTIVE" && i.health && !i.health.ok).length;

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero icon={AppWindow} title="App Installer" description="Install reviewed open-source apps into your websites. Every release is downloaded from its vendor and checked against the vendor's checksum." />
      <div className="mt-[15px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
        <StatCard icon={Boxes} tile="teal" label="Apps you can install" value={catalog.data ? catalog.data.items.filter((a) => a.compatibility.installable).length : "—"} sub={catalog.data ? `${catalog.data.items.length} in the catalog` : undefined} />
        <StatCard icon={LayoutGrid} tile="blue" label="Your installations" value={installs.data ? items.length : "—"} sub={installs.data ? `${items.filter((i) => i.status === "ACTIVE").length} active` : undefined} />
        <StatCard icon={ArrowUpCircle} tile="orange" label="Updates available" value={installs.data ? updates : "—"} sub="Approved releases" />
        <StatCard icon={ShieldCheck} tile="green" label="Health" value={installs.data ? (unhealthy ? `${unhealthy} need attention` : "All answering") : "—"} sub="Last check of each app" />
      </div>
      <Toolbar>
        <FilterTabs label="App Installer views" value={tab} onChange={(v) => setParams(v === "browse" ? {} : { tab: v })} items={[["browse", "Browse apps"], ["installed", `My installations${installs.data ? ` (${items.length})` : ""}`]]} />
      </Toolbar>
      {tab === "browse" ? <Browse q={catalog} /> : <Installed q={installs} />}
    </div>
  );
}

function Browse({ q }: { q: ReturnType<typeof useQuery<{ items: AppCatalogItem[] }>> }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const apps = useMemo(() => q.data?.items ?? [], [q.data]);
  const categories = useMemo(() => [...new Set(apps.map((a) => a.category))], [apps]);
  const shown = apps.filter((a) => (category === "all" || a.category === category) && `${a.name} ${a.summary} ${a.category}`.toLowerCase().includes(search.trim().toLowerCase()));
  const featured = apps.filter((a) => a.featured);
  const popular = [...apps].filter((a) => a.installCount > 0).sort((a, b) => b.installCount - a.installCount).slice(0, 4);

  if (q.error) return <div className="hd-card mt-[15px] p-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (q.isPending) return <div className="mt-[15px] grid gap-[15px] md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-44 rounded-xl" />)}</div>;
  if (!apps.length) return <div className="mt-[15px]"><EmptyCard icon={AppWindow} title="No apps are available right now" body="The administrators have not enabled any apps for installation yet." /></div>;
  return (
    <>
      {!search && category === "all" && featured.length > 0 && (
        <section className="mt-[15px]" aria-label="Featured">
          <h2 className="mb-[10px] flex items-center gap-2 text-[14.5px] font-bold text-fg"><Sparkles size={16} className="text-accent" />Featured</h2>
          <div className="grid gap-[15px] md:grid-cols-2 xl:grid-cols-3">{featured.map((a) => <AppCard key={a.id} a={a} />)}</div>
        </section>
      )}
      {!search && category === "all" && popular.length > 0 && (
        <section className="mt-[19px]" aria-label="Popular on HostDock">
          <h2 className="mb-[10px] text-[14.5px] font-bold text-fg">Popular on HostDock</h2>
          <div className="grid gap-[15px] md:grid-cols-2 xl:grid-cols-4">{popular.map((a) => <AppCard key={a.id} a={a} compact />)}</div>
        </section>
      )}
      <section className="mt-[19px]" aria-label="All apps">
        <div className="mb-[10px] flex flex-wrap items-center gap-[11px]">
          <h2 className="mr-auto text-[14.5px] font-bold text-fg">All apps</h2>
          <SearchBox value={search} onChange={setSearch} placeholder="Search apps" />
          <select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-[35px] rounded-lg border border-line bg-surface px-3 text-[12.5px] text-fg">
            <option value="all">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] ?? c}</option>)}
          </select>
        </div>
        {shown.length ? <div className="grid gap-[15px] md:grid-cols-2 xl:grid-cols-3">{shown.map((a) => <AppCard key={a.id} a={a} />)}</div>
          : <EmptyCard title="No app matches" body="Try another name or category." />}
      </section>
      <p className="mt-[19px] text-[12px] text-muted">
        Only apps whose installer HostDock has implemented and tested are listed. More apps are being prepared; they appear here once they can be installed safely.
      </p>
    </>
  );
}

function AppCard({ a, compact }: { a: AppCatalogItem; compact?: boolean }) {
  return (
    <Link to={`/apps/${a.id}`} className="hd-card group flex flex-col gap-3 p-[16px] transition-shadow hover:shadow-md">
      <div className="flex items-start gap-3">
        <CatalogAppLogo logo={a.logo} name={a.name} size="md" />
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate text-[14.5px] font-bold text-fg">{a.name}</p>
          <p className="mt-[3px] text-[12px] text-muted">{CATEGORY_LABEL[a.category] ?? a.category}{a.versions[0] ? ` · ${a.versions[0].version}` : ""}</p>
        </div>
        {a.installCount > 0 && <span className="text-[11.5px] text-subtle tabular-nums" title="Active installations on HostDock">{a.installCount} installed</span>}
      </div>
      {!compact && <p className="line-clamp-2 text-[12.5px] leading-[18px] text-muted">{a.summary}</p>}
      <div className="mt-auto flex flex-wrap items-center gap-2">
        {a.compatibility.installable ? <Pill tone="ok" dot>Ready to install</Pill> : (
          <span className="flex items-center gap-1.5 text-[12px] text-warning" title={a.compatibility.reasons.join("\n")}><TriangleAlert size={14} />{a.compatibility.reasons[0]}</span>
        )}
        {a.presetOf && <Pill tone="info">Uses the {a.presetOf === "wordpress" ? "WordPress" : a.presetOf} engine</Pill>}
      </div>
    </Link>
  );
}

function Installed({ q }: { q: ReturnType<typeof useQuery<{ items: AppInstallationRecord[] }>> }) {
  if (q.error) return <div className="hd-card mt-[15px] p-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (q.isPending) return <Skeleton className="mt-[15px] h-40 w-full rounded-xl" />;
  const items = q.data?.items ?? [];
  if (!items.length) return <div className="mt-[15px]"><EmptyCard icon={LayoutGrid} title="No apps installed yet" body="Pick an app from the catalog to install it into one of your websites." action={<Link to="/apps" className="text-[13px] font-medium text-accent hover:underline">Browse apps</Link>} /></div>;
  return (
    <ul className="mt-[15px] grid gap-[15px] md:grid-cols-2 xl:grid-cols-3">
      {items.map((i) => (
        <li key={i.id} className="hd-card flex flex-col gap-3 p-[16px]">
          <div className="flex items-start gap-3">
            <CatalogAppLogo logo={i.logo} name={i.appName} size="md" />
            <div className="min-w-0 flex-1 leading-tight">
              <Link to={`/apps/installations/${i.id}`} className="block truncate text-[14.5px] font-bold text-fg hover:underline">{i.appName} <span className="font-normal text-muted">{i.version}</span></Link>
              <a href={i.url} target="_blank" rel="noreferrer" className="mt-[3px] flex items-center gap-1 truncate text-[12px] text-accent hover:underline">{i.domain}{i.path ? `/${i.path}` : ""}<ExternalLink size={11} /></a>
            </div>
            <Pill tone={STATUS[i.status].tone} dot>{busy(i.status) && <Loader2 size={12} className="animate-spin" />}{STATUS[i.status].label}</Pill>
          </div>
          <dl className="grid grid-cols-2 gap-2 text-[12px]">
            <div><dt className="text-subtle">Health</dt><dd className={cn("font-medium", i.health && !i.health.ok && "text-danger")}>{i.health ? (i.health.ok ? "Answering" : "Not answering") : "Not checked yet"}</dd></div>
            <div><dt className="text-subtle">Last backup</dt><dd className="font-medium">{i.backup ? formatRelative(i.backup.at) : "None"}</dd></div>
            <div><dt className="text-subtle">Update</dt><dd className="font-medium">{i.update.kind === "none" ? "Up to date" : `${i.update.version} (${i.update.kind === "major" ? "major" : "minor"})`}</dd></div>
            <div><dt className="text-subtle">Installed</dt><dd className="font-medium">{i.installedAt ? formatRelative(i.installedAt) : "—"}</dd></div>
          </dl>
          {i.statusMessage && <p className={cn("line-clamp-2 text-[12px]", i.status === "FAILED" ? "text-danger" : "text-muted")} title={i.statusMessage}>{i.statusMessage}</p>}
          <Link to={`/apps/installations/${i.id}`} className="mt-auto inline-flex h-[34px] items-center justify-center rounded-lg border border-line-strong text-[12.5px] font-medium hover:bg-surface-2">Manage</Link>
        </li>
      ))}
    </ul>
  );
}
